import {
  api,
  useDialog,
  useMutation,
  useQueryClient,
  useRoleConf,
  useToaster,
} from "@pharmupp/p3-front-commons"
import { useParams } from "react-router-dom"
import { userTypesReferential } from "../../referential"
import { getUpdateUserQueryKey } from "../UpdateUser"
import { serverToForm } from "../UpdateUser/userForm.adapter"
import { apiErrorHandlerConfig } from "../apiErrorHandlerConfig"
import { apiRoleConf } from "../apiRoleConf"
import type { ApiUserModel } from "../models"
import { type UnhandledError, useApiErrorHandler } from "../useApiErrorHandler"

export const useUserFormApi = () => {
  const queryClient = useQueryClient()
  const toaster = useToaster()
  const dialog = useDialog()
  const { id } = useParams()
  const isUpdate = !!id
  const apiConf = useRoleConf(apiRoleConf)
  const apiErrorHandler = useApiErrorHandler(apiErrorHandlerConfig)

  // Send values
  const { mutateAsync: sendValues } = useMutation({
    mutationFn: (formValues: string) =>
      api<ApiUserModel>(`${apiConf.resourceEndpoint}/${id || ""}`, {
        method: isUpdate ? "PUT" : "POST",
        headers: { "Content-Type": "application/json" },
        body: formValues,
      }).then(serverToForm),
    onMutate: () => toaster.info("Requête en cours..."),
    onSuccess() {
      toaster.success("Sauvegarde effectuée")
      queryClient.invalidateQueries({ queryKey: ["users", "form", id] })
    },
    onError() {
      toaster.error("Une erreur est survenue")
    },
  })

  const deleteAction = (onSuccess: () => void) => {
    dialog.delete({
      endpoint: `${apiConf.resourceEndpoint}/${id}`,
      onSuccess,
      onError: (error: unknown) =>
        apiErrorHandler.handleDeleteError(error as UnhandledError),
    })
  }

  const activate = (onError: (error: unknown) => void) =>
    dialog.activate<ApiUserModel>({
      endpoint: `${apiConf.resourceEndpoint}/${id}/activate`,
      onSuccess: (updatedValues: ApiUserModel) =>
        queryClient.setQueryData<ApiUserModel>(
          getUpdateUserQueryKey(id),
          updatedValues,
        ),
      onError,
    })

  const deactivate = (onError: (error: unknown) => void) =>
    dialog.deactivate<ApiUserModel>({
      endpoint: `${apiConf.resourceEndpoint}/${id}/deactivate`,
      onSuccess: (updatedValues: ApiUserModel) =>
        queryClient.setQueryData<ApiUserModel>(
          getUpdateUserQueryKey(id),
          updatedValues,
        ),
      onError,
    })

  const referential: UserFormReferential = {
    type: userTypesReferential,
  }

  return {
    referential,
    sendValues,
    deleteAction,
    activate,
    deactivate,
  }
}

export interface UserFormReferential {
  type: typeof userTypesReferential
}
